import type { MenuProps } from 'antd'
import { Menu } from 'antd'
import React, { useMemo } from 'react'
import { Link } from 'react-router'
import { MenuDataProps } from '@/components/AntdForm/antd-components.def.ts'
import { useAuth } from '@/hooks/useAuth.ts'
import { intersection } from 'lodash-es'
import { ModuleFunction } from '@/features/auth/auth.def.ts'
import { navMenuData } from '@/router/routes.tsx'

type MenuItem = Required<MenuProps>['items'][number];

type PermissionChecker = (x: ModuleFunction[]) => boolean

const getMenuItemVisibility = ({
  allowRoles,
  userRoles,
  requiredPermissions = [],
  anyOfPermissions = [],
  checkPermissions,
  checkAnyPermissions,
}: {
  allowRoles?: string[]
  userRoles: string[]
  requiredPermissions?: ModuleFunction[]
  anyOfPermissions?: ModuleFunction[]
  checkPermissions: PermissionChecker
  checkAnyPermissions: PermissionChecker
}): boolean => {
  let access = true

  if (allowRoles?.length && !intersection(allowRoles, userRoles).length) {
    access = false
  }

  if (requiredPermissions?.length && !checkPermissions(requiredPermissions)) {
    access = false
  }

  if (anyOfPermissions?.length && !checkAnyPermissions(anyOfPermissions)) {
    access = false
  }

  return access
}

const menuDataToItems = (data: MenuDataProps[], parentKey = '', userRoles: string[] = [], checkPermissions: PermissionChecker, checkAnyPermissions: PermissionChecker): MenuItem[] => {
  return data.filter(x => getMenuItemVisibility({
    allowRoles: x.allowRoles,
    userRoles,
    requiredPermissions: x.requiredPermissions,
    anyOfPermissions: x.anyOfPermissions,
    checkPermissions,
    checkAnyPermissions,
  })).map(
    x => ({
      key: parentKey ? `${parentKey}.${x.key}` : x.key,
      label: x.url ? <Link to={x.url} style={{ color: 'inherit' }} target={x.target}>{x.label}</Link> : x.label,
      icon: x.icon,
      children: x.children?.length ? menuDataToItems(x.children, x.key, userRoles, checkPermissions, checkAnyPermissions) : undefined,
    }))
}

export const NavMenu: React.FC = () => {
  const { user, checkHasAllPermissions, checkHasAnyPermissions } = useAuth()

  const navItems = useMemo(() => {
    return menuDataToItems(navMenuData, '', user?.roles || [], checkHasAllPermissions, checkHasAnyPermissions)
  }, [checkHasAllPermissions, checkHasAnyPermissions, user?.roles])

  return (
    <Menu
      theme="dark"
      selectable={false}
      items={navItems}
      mode="inline"
      className="bg-inherit [--ant-menu-item-color:inherit]"
    />
  )
}